/**
 * Warehouse-Line / Fund-Provider DTOs (Issue #1631)
 *
 * Zod schemas and TypeScript types for the three-level capital-source hierarchy:
 *   CapitalProvider → WarehouseLine → WarehouseLineLoanTypeProfile
 *
 * Plus credit-box rules, required documents, global rules, exception requests,
 * and platform-default seed templates.
 *
 * Phase 0.3 decisions (D1–D19) baked in. See proposal §3 + the implementation
 * plan at docs/superpowers/plans/2026-05-06-1631-warehouse-line-m1m5.md.
 */

import { z } from 'zod';

// ============================================================================
// SHARED PRIMITIVES
// ============================================================================

const NonEmptyString = z.string().min(1);
const Uuid = z.string().uuid();

// ============================================================================
// WAREHOUSE-LINE LOAN CATEGORY (M2 — also closes M2R-5 / M2Q-8)
// ============================================================================

/**
 * Canonical loan-category values for the warehouse-line / fund-provider
 * surface. Matches the uppercase wire enum emitted by
 * 33-pricing-engine-service for `loan_category` on SizingRun /
 * WarehouseLineLoanTypeProfile. Use this in place of free-form
 * `z.string().min(1)` so the gateway rejects bogus categories before they
 * hit the engine.
 *
 * Note: distinct from the lowercase `LOAN_CATEGORY_VALUES` in
 * `utils/loan-attribute-projection` — that one normalizes case-level loan
 * attributes (lead/intake form data) and includes 'custom' as a passthrough
 * bucket. The warehouse-line surface is strict: every persisted profile is
 * one of these canonical buckets.
 *
 * `OTHER` covers profiles ingested via M3 AI extraction whose loan-product
 * label doesn't map cleanly to one of the four primary buckets — without it,
 * the gateway Zod gate rejects every `OTHER`-category sizing request and
 * those AI-ingested profiles are permanently unsizeable through the portal.
 * Admins can re-key OTHER → primary bucket via the editor when reviewing.
 */
export const WAREHOUSE_LINE_LOAN_CATEGORY_VALUES = ['BRIDGE', 'DSCR', 'GROUND_UP', 'FIX_AND_FLIP', 'OTHER'] as const;
export const LoanCategorySchema = z.enum(WAREHOUSE_LINE_LOAN_CATEGORY_VALUES);
export type LoanCategory = z.infer<typeof LoanCategorySchema>;

// ============================================================================
// CAPITAL PROVIDER
// ============================================================================

export const CapitalProviderSchema = z.object({
  id: Uuid,
  businessId: Uuid,
  name: NonEmptyString,
  legalName: z.string().nullable(),
  contactEmail: z.string().email().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(), // ISO timestamp
  updatedAt: z.string(),
});
export type CapitalProvider = z.infer<typeof CapitalProviderSchema>;

export const UpsertCapitalProviderInputSchema = z.object({
  id: Uuid.optional(), // omit on create
  name: NonEmptyString,
  legalName: z.string().optional(),
  contactEmail: z.string().email().optional(),
  notes: z.string().optional(),
});
export type UpsertCapitalProviderInput = z.infer<typeof UpsertCapitalProviderInputSchema>;

// ============================================================================
// WAREHOUSE LINE
// ============================================================================

export const WarehouseLineStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'ARCHIVED']);
export type WarehouseLineStatus = z.infer<typeof WarehouseLineStatusSchema>;

export const WarehouseLineSchema = z.object({
  id: Uuid,
  businessId: Uuid,
  capitalProviderId: Uuid,
  name: NonEmptyString,
  internalCode: z.string().nullable(),
  status: WarehouseLineStatusSchema,
  facilityCap: z.string().nullable(), // Decimal serialized as string
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WarehouseLine = z.infer<typeof WarehouseLineSchema>;

export const UpsertWarehouseLineInputSchema = z.object({
  id: Uuid.optional(),
  capitalProviderId: Uuid,
  name: NonEmptyString,
  internalCode: z.string().optional(),
  status: WarehouseLineStatusSchema.optional(),
  facilityCap: z.string().optional(), // accept decimal as string from form
  notes: z.string().optional(),
});
export type UpsertWarehouseLineInput = z.infer<typeof UpsertWarehouseLineInputSchema>;

// ============================================================================
// PROFILE — SCD2 versioning per D1
// ============================================================================

export const WarehouseLineProfileStateSchema = z.enum(['DRAFT', 'SCHEDULED', 'ACTIVE', 'SUPERSEDED', 'ARCHIVED']);
export type WarehouseLineProfileState = z.infer<typeof WarehouseLineProfileStateSchema>;

// ----- D9 Pricing adjustments -----
export const PricingAdjustmentConditionSchema = z.object({
  field: NonEmptyString,
  op: z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'in', 'notIn', 'matches']),
  value: z.unknown(), // value shape depends on op (number / [min,max] / string[])
});
export type PricingAdjustmentCondition = z.infer<typeof PricingAdjustmentConditionSchema>;

export const PricingAdjustmentSchema = z.object({
  id: NonEmptyString, // stable id so admins can edit
  description: z.string().optional(),
  condition: PricingAdjustmentConditionSchema,
  adjustment: z.object({
    type: z.enum(['rate_bps', 'points']),
    amount: z.number(),
  }),
});
export type PricingAdjustment = z.infer<typeof PricingAdjustmentSchema>;

// ----- D16 Prepayment penalty -----
export const PrepaymentPenaltySchema = z.object({
  structure: z.enum(['STEP_DOWN', 'FLAT', 'NONE']),
  // STEP_DOWN: schedule of (months, percent) tiers
  schedule: z
    .array(
      z.object({
        afterMonths: z.number().int().nonnegative(),
        percent: z.number().min(0).max(100),
      }),
    )
    .optional(),
  // FLAT: single percent + duration
  flatPercent: z.number().min(0).max(100).optional(),
  flatMonths: z.number().int().nonnegative().optional(),
});
export type PrepaymentPenalty = z.infer<typeof PrepaymentPenaltySchema>;

// ----- D17 Insurance requirements -----
export const InsuranceCoverageSchema = z.object({
  minCoverage: z.number().nonnegative().optional(),
  namedInsuredLanguage: z.string().optional(),
  maxDeductible: z.number().nonnegative().optional(),
  required: z.boolean().default(true),
});
export const InsuranceRequirementsSchema = z.object({
  hazard: InsuranceCoverageSchema.optional(),
  liability: InsuranceCoverageSchema.optional(),
  flood: InsuranceCoverageSchema.optional(),
  // additional kinds free-form, e.g. builder's risk
  additional: z.record(z.string(), InsuranceCoverageSchema).optional(),
});
export type InsuranceRequirements = z.infer<typeof InsuranceRequirementsSchema>;

// ----- D10 CTF templates copied to case_conditions_to_fund on pin -----
export const CtfTemplateSchema = z.object({
  key: NonEmptyString,
  title: NonEmptyString,
  description: z.string().optional(),
  defaultDueOffsetDays: z.number().int().optional(),
});
export type CtfTemplate = z.infer<typeof CtfTemplateSchema>;

// ----- Profile (full row) -----
export const WarehouseLineLoanTypeProfileSchema = z.object({
  id: Uuid,
  businessId: Uuid,
  warehouseLineId: Uuid,
  loanCategory: NonEmptyString, // BRIDGE | DSCR | GROUND_UP | FIX_AND_FLIP | ...
  // SCD2 versioning
  version: z.number().int().positive(),
  parentProfileId: Uuid.nullable(),
  state: WarehouseLineProfileStateSchema,
  effectiveFrom: z.string().nullable(),
  effectiveUntil: z.string().nullable(),
  // Leverage caps
  maxLtvPercent: z.number().nullable(),
  maxLtcPercent: z.number().nullable(),
  maxLtarvPercent: z.number().nullable(),
  minFico: z.number().int().nullable(),
  minLoanAmount: z.string().nullable(), // decimal
  maxLoanAmount: z.string().nullable(),
  // Per-deal economics
  advanceRatePercent: z.number().min(0).max(1),
  personalGuaranteeRequired: z.boolean(),
  prepaymentPenalty: PrepaymentPenaltySchema.nullable(),
  insuranceRequirements: InsuranceRequirementsSchema.nullable(),
  maxHoldPeriodDays: z.number().int().nullable(),
  takeoutWindowDays: z.number().int().nullable(),
  // Required canonical fields
  requiredFields: z.array(NonEmptyString),
  // Pricing
  rateTierVersionId: Uuid.nullable(),
  pointsTierId: Uuid.nullable(),
  defaultTermMonths: z.number().int().nullable(),
  pricingAdjustments: z.array(PricingAdjustmentSchema).nullable(),
  // CTF templates
  defaultCtfs: z.array(CtfTemplateSchema).nullable(),
  // Provenance
  sourceDocumentId: Uuid.nullable(),
  // Audit
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string().nullable(),
  supersededAt: z.string().nullable(),
});
export type WarehouseLineLoanTypeProfile = z.infer<typeof WarehouseLineLoanTypeProfileSchema>;

// ----- Profile create / update inputs -----
// Create lands as DRAFT, version=1, parentProfileId=null. Server enforces the
// (warehouseLineId, loanCategory) uniqueness on first version.
export const CreateProfileInputSchema = z.object({
  warehouseLineId: Uuid,
  loanCategory: NonEmptyString,
  maxLtvPercent: z.number().optional(),
  maxLtcPercent: z.number().optional(),
  maxLtarvPercent: z.number().optional(),
  minFico: z.number().int().optional(),
  minLoanAmount: z.string().optional(),
  maxLoanAmount: z.string().optional(),
  advanceRatePercent: z.number().min(0).max(1).default(1.0),
  personalGuaranteeRequired: z.boolean().default(true),
  prepaymentPenalty: PrepaymentPenaltySchema.optional(),
  insuranceRequirements: InsuranceRequirementsSchema.optional(),
  maxHoldPeriodDays: z.number().int().optional(),
  takeoutWindowDays: z.number().int().optional(),
  requiredFields: z.array(NonEmptyString).default([]),
  rateTierVersionId: Uuid.optional(),
  pointsTierId: Uuid.optional(),
  defaultTermMonths: z.number().int().optional(),
  pricingAdjustments: z.array(PricingAdjustmentSchema).optional(),
  defaultCtfs: z.array(CtfTemplateSchema).optional(),
  sourceDocumentId: Uuid.optional(),
});
export type CreateProfileInput = z.infer<typeof CreateProfileInputSchema>;

// Edit DRAFT profile in place. Cannot be called on ACTIVE / SUPERSEDED rows
// (server returns INVALID_ARGUMENT — caller must use CreateNewVersion instead).
export const UpdateProfileInputSchema = CreateProfileInputSchema.partial().extend({
  id: Uuid,
});
export type UpdateProfileInput = z.infer<typeof UpdateProfileInputSchema>;

// Forks a new version from an existing chain — increments version, sets
// parentProfileId = chainRoot(parent), lands as DRAFT.
export const CreateNewVersionInputSchema = z.object({
  parentProfileVersionId: Uuid,
  payload: CreateProfileInputSchema.omit({ warehouseLineId: true, loanCategory: true }).partial(),
});
export type CreateNewVersionInput = z.infer<typeof CreateNewVersionInputSchema>;

// Lifecycle inputs (D1 SCD2)
export const ScheduleProfileInputSchema = z.object({
  id: Uuid,
  effectiveFrom: z.string(),
});
export type ScheduleProfileInput = z.infer<typeof ScheduleProfileInputSchema>;

export const ActivateProfileInputSchema = z.object({ id: Uuid });
export type ActivateProfileInput = z.infer<typeof ActivateProfileInputSchema>;

export const ArchiveProfileInputSchema = z.object({ id: Uuid });
export type ArchiveProfileInput = z.infer<typeof ArchiveProfileInputSchema>;

// ============================================================================
// CREDIT BOX RULE
// ============================================================================

export const CreditBoxRuleOpSchema = z.enum([
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'in',
  'notIn',
  'matches',
  'set',
]);
export type CreditBoxRuleOp = z.infer<typeof CreditBoxRuleOpSchema>;

export const CreditBoxRuleSeveritySchema = z.enum(['BLOCK', 'WARN', 'INFO']);
export type CreditBoxRuleSeverity = z.infer<typeof CreditBoxRuleSeveritySchema>;

export const WarehouseLineCitationSchema = z.object({
  documentId: Uuid.optional(),
  page: z.number().int().nonnegative().optional(),
  quote: z.string().optional(),
});
export type WarehouseLineCitation = z.infer<typeof WarehouseLineCitationSchema>;

export const CreditBoxRuleSchema = z.object({
  id: Uuid,
  businessId: Uuid,
  profileVersionId: Uuid,
  // D18 — array dereference syntax: 'guarantors[].fico::min'
  field: NonEmptyString,
  op: CreditBoxRuleOpSchema,
  value: z.unknown(),
  severity: CreditBoxRuleSeveritySchema,
  message: z.string().nullable(),
  citationDocumentId: Uuid.nullable(),
  citationPage: z.number().int().nullable(),
  citationQuote: z.string().nullable(),
  createdAt: z.string(),
});
export type CreditBoxRule = z.infer<typeof CreditBoxRuleSchema>;

export const UpsertCreditBoxRuleInputSchema = z.object({
  id: Uuid.optional(),
  profileVersionId: Uuid,
  field: NonEmptyString,
  op: CreditBoxRuleOpSchema,
  value: z.unknown(),
  severity: CreditBoxRuleSeveritySchema.default('BLOCK'),
  message: z.string().optional(),
  citation: WarehouseLineCitationSchema.optional(),
});
export type UpsertCreditBoxRuleInput = z.infer<typeof UpsertCreditBoxRuleInputSchema>;

// ============================================================================
// REQUIRED DOC
// ============================================================================

export const WarehouseLineRequiredDocSchema = z.object({
  id: Uuid,
  businessId: Uuid,
  profileVersionId: Uuid,
  documentTypeKey: NonEmptyString,
  required: z.boolean(),
  trigger: z.unknown().nullable(), // optional gating condition (same shape as PricingAdjustmentCondition)
  citationDocumentId: Uuid.nullable(),
  citationPage: z.number().int().nullable(),
  citationQuote: z.string().nullable(),
  createdAt: z.string(),
});
export type WarehouseLineRequiredDoc = z.infer<typeof WarehouseLineRequiredDocSchema>;

export const UpsertRequiredDocInputSchema = z.object({
  id: Uuid.optional(),
  profileVersionId: Uuid,
  documentTypeKey: NonEmptyString,
  required: z.boolean().default(true),
  trigger: PricingAdjustmentConditionSchema.optional(),
  citation: WarehouseLineCitationSchema.optional(),
});
export type UpsertRequiredDocInput = z.infer<typeof UpsertRequiredDocInputSchema>;

// ============================================================================
// GLOBAL RULE (D7) — line-level, applies to every profile under the line
// ============================================================================

export const WarehouseLineGlobalRuleSchema = z.object({
  id: Uuid,
  businessId: Uuid,
  warehouseLineId: Uuid,
  field: NonEmptyString,
  op: CreditBoxRuleOpSchema,
  value: z.unknown(),
  severity: CreditBoxRuleSeveritySchema,
  message: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
  effectiveUntil: z.string().nullable(),
  createdAt: z.string(),
});
export type WarehouseLineGlobalRule = z.infer<typeof WarehouseLineGlobalRuleSchema>;

export const UpsertGlobalRuleInputSchema = z.object({
  id: Uuid.optional(),
  warehouseLineId: Uuid,
  field: NonEmptyString,
  op: CreditBoxRuleOpSchema,
  value: z.unknown(),
  severity: CreditBoxRuleSeveritySchema.default('BLOCK'),
  message: z.string().optional(),
  effectiveFrom: z.string().optional(),
  effectiveUntil: z.string().optional(),
});
export type UpsertGlobalRuleInput = z.infer<typeof UpsertGlobalRuleInputSchema>;

// ============================================================================
// EXCEPTION REQUEST (D3) — REQUESTED → APPROVED | DENIED | EXPIRED
// ============================================================================

export const WarehouseLineExceptionStateSchema = z.enum(['REQUESTED', 'APPROVED', 'DENIED', 'EXPIRED']);
export type WarehouseLineExceptionState = z.infer<typeof WarehouseLineExceptionStateSchema>;

export const WarehouseLineExceptionRequestSchema = z.object({
  id: Uuid,
  businessId: Uuid,
  caseId: Uuid,
  profileVersionId: Uuid,
  blockingRuleIds: z.array(Uuid),
  state: WarehouseLineExceptionStateSchema,
  requesterId: Uuid,
  approverId: Uuid.nullable(),
  reason: NonEmptyString,
  decisionNotes: z.string().nullable(),
  expiresAt: z.string().nullable(),
  decidedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WarehouseLineExceptionRequest = z.infer<typeof WarehouseLineExceptionRequestSchema>;

export const RequestExceptionInputSchema = z.object({
  caseId: Uuid,
  profileVersionId: Uuid,
  blockingRuleIds: z.array(Uuid).default([]),
  reason: NonEmptyString,
  expiresAt: z.string().optional(),
});
export type RequestExceptionInput = z.infer<typeof RequestExceptionInputSchema>;

export const ApproveExceptionInputSchema = z.object({
  id: Uuid,
  decisionNotes: z.string().optional(),
});
export type ApproveExceptionInput = z.infer<typeof ApproveExceptionInputSchema>;

export const DenyExceptionInputSchema = z.object({
  id: Uuid,
  decisionNotes: NonEmptyString, // require a reason on denial
});
export type DenyExceptionInput = z.infer<typeof DenyExceptionInputSchema>;

// ============================================================================
// SEED TEMPLATE (D12) — platform-default, no businessId per CLAUDE.md §2f
// ============================================================================

export const WarehouseLineSeedPayloadSchema = z.object({
  capitalProvider: z.object({
    name: NonEmptyString,
    legalName: z.string().optional(),
    notes: z.string().optional(),
  }),
  warehouseLine: z.object({
    name: NonEmptyString,
    internalCode: z.string().optional(),
    notes: z.string().optional(),
  }),
  profile: CreateProfileInputSchema.omit({ warehouseLineId: true }),
  rules: z
    .array(
      z.object({
        field: NonEmptyString,
        op: CreditBoxRuleOpSchema,
        value: z.unknown(),
        severity: CreditBoxRuleSeveritySchema.default('BLOCK'),
        message: z.string().optional(),
      }),
    )
    .default([]),
  requiredDocs: z
    .array(
      z.object({
        documentTypeKey: NonEmptyString,
        required: z.boolean().default(true),
      }),
    )
    .default([]),
  globalRules: z
    .array(
      z.object({
        field: NonEmptyString,
        op: CreditBoxRuleOpSchema,
        value: z.unknown(),
        severity: CreditBoxRuleSeveritySchema.default('BLOCK'),
        message: z.string().optional(),
      }),
    )
    .default([]),
});
export type WarehouseLineSeedPayload = z.infer<typeof WarehouseLineSeedPayloadSchema>;

export const WarehouseLineSeedSchema = z.object({
  id: Uuid,
  templateKey: NonEmptyString,
  providerName: NonEmptyString,
  lineName: NonEmptyString,
  loanCategory: NonEmptyString,
  payload: WarehouseLineSeedPayloadSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WarehouseLineSeed = z.infer<typeof WarehouseLineSeedSchema>;

export const ApplySeedToBusinessInputSchema = z.object({
  templateKey: NonEmptyString,
});
export type ApplySeedToBusinessInput = z.infer<typeof ApplySeedToBusinessInputSchema>;

// ============================================================================
// CASE CONDITIONS TO FUND (D10) — owned by 29-case-management-service
// ============================================================================

export const CaseConditionStatusSchema = z.enum(['OPEN', 'IN_PROGRESS', 'SATISFIED', 'WAIVED']);
export type CaseConditionStatus = z.infer<typeof CaseConditionStatusSchema>;

export const CaseConditionToFundSchema = z.object({
  id: Uuid,
  businessId: Uuid,
  caseId: Uuid,
  loanTypeProfileVersionId: Uuid.nullable(),
  templateKey: z.string().nullable(),
  title: NonEmptyString,
  description: z.string().nullable(),
  status: CaseConditionStatusSchema,
  ownerId: Uuid.nullable(),
  ownerName: z.string().nullable(),
  dueDate: z.string().nullable(),
  satisfiedAt: z.string().nullable(),
  satisfiedBy: Uuid.nullable(),
  satisfiedEvidence: z.unknown().nullable(),
  waivedAt: z.string().nullable(),
  waivedBy: Uuid.nullable(),
  waivedReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: Uuid,
});
export type CaseConditionToFund = z.infer<typeof CaseConditionToFundSchema>;

export const CreateCtfInputSchema = z.object({
  caseId: Uuid,
  loanTypeProfileVersionId: Uuid.optional(),
  templateKey: z.string().optional(),
  title: NonEmptyString,
  description: z.string().optional(),
  ownerId: Uuid.optional(),
  dueDate: z.string().optional(),
});
export type CreateCtfInput = z.infer<typeof CreateCtfInputSchema>;

export const UpdateCtfInputSchema = z.object({
  id: Uuid,
  title: z.string().optional(),
  description: z.string().optional(),
  ownerId: Uuid.optional(),
  dueDate: z.string().optional(),
});
export type UpdateCtfInput = z.infer<typeof UpdateCtfInputSchema>;

export const SatisfyCtfInputSchema = z.object({
  id: Uuid,
  evidence: z
    .object({
      documentId: Uuid.optional(),
      externalRef: z.string().optional(),
      notes: z.string().optional(),
    })
    .optional(),
});
export type SatisfyCtfInput = z.infer<typeof SatisfyCtfInputSchema>;

export const WaiveCtfInputSchema = z.object({
  id: Uuid,
  reason: NonEmptyString,
});
export type WaiveCtfInput = z.infer<typeof WaiveCtfInputSchema>;

// ============================================================================
// LIST INPUTS (cursor-style pagination)
// ============================================================================

export const ListByBusinessInputSchema = z.object({
  // businessId is taken from auth context, not from the wire
  limit: z.number().int().positive().max(200).default(50),
  cursor: z.string().optional(),
});
export type ListByBusinessInput = z.infer<typeof ListByBusinessInputSchema>;

export const ListProfilesByLineInputSchema = ListByBusinessInputSchema.extend({
  warehouseLineId: Uuid,
  loanCategory: z.string().optional(),
  state: WarehouseLineProfileStateSchema.optional(),
});
export type ListProfilesByLineInput = z.infer<typeof ListProfilesByLineInputSchema>;

export const ListByLineInputSchema = ListByBusinessInputSchema.extend({
  warehouseLineId: Uuid,
});
export type ListByLineInput = z.infer<typeof ListByLineInputSchema>;

export const ListExceptionsInputSchema = ListByBusinessInputSchema.extend({
  state: WarehouseLineExceptionStateSchema.optional(),
  caseId: Uuid.optional(),
});
export type ListExceptionsInput = z.infer<typeof ListExceptionsInputSchema>;

export const ListCtfsInputSchema = ListByBusinessInputSchema.extend({
  caseId: Uuid.optional(),
  status: CaseConditionStatusSchema.optional(),
});
export type ListCtfsInput = z.infer<typeof ListCtfsInputSchema>;
